import { canvasSteps, type audience } from '@bb/contracts';

/**
 * Decision Split settings as the editor reads and writes them (BB-125): `canvasSteps.DecisionSplitConfig` of
 * `@bb/contracts` — one audience; users who match go down `yes`, the rest down `no`.
 */
export type DecisionSplitConfig = canvasSteps.DecisionSplitConfig;
type Definition = audience.AudienceDefinition;
type FilterNode = audience.FilterNode;

/** The ports, Yes first: the order the node shows them. */
export const DECISION_SPLIT_PORTS = canvasSteps.DECISION_SPLIT_PORTS;

/** What a new Decision Split holds (the editor catalog's default): no filters, every user goes down Yes. */
export const emptyDecisionAudience = (): Definition => ({ schemaVersion: 1, include: { kind: 'and', id: 'root', children: [] }, exclude: [], apps: { mode: 'all' } });

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The stored settings; an audience that is not an object yet reads as the empty one (the server checks the rest). */
export function readDecisionSplit(raw: unknown): DecisionSplitConfig {
  const c = isObject(raw) ? raw : {};
  const a = isObject(c.audience) ? c.audience : null;
  if (!a || !isObject(a.include) || !Array.isArray(a.exclude) || !isObject(a.apps)) return { audience: emptyDecisionAudience() };
  return { audience: a as unknown as Definition };
}

/** Filters in a node, however deep. */
export function countFilters(node: FilterNode): number {
  switch (node.kind) {
    case 'filter':
      return 1;
    case 'not':
      return countFilters(node.child);
    default:
      return node.children.reduce((n, child) => n + countFilters(child), 0);
  }
}

/** No filter, no exclusion and every app: every user who reaches the step goes down Yes. */
export const matchesEveryone = (a: Definition) => countFilters(a.include) === 0 && a.exclude.every((x) => countFilters(x) === 0) && a.apps.mode === 'all';
